import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Container, Paper, Button, Box,
  IconButton, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Alert, Snackbar, Avatar, Menu,
} from '@mui/material';
import { LocalHospital, ArrowBack, Add, Delete, Edit, ExitToApp } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { userService, authService } from '../../services/api';

const ROLES = ['Receptionist', 'LabAttendant', 'Manager', 'Admin'];

const emptyForm = {
  username: '', password: '', firstName: '', lastName: '',
  email: '', phoneNumber: '', role: 'Receptionist',
};

const StaffManagement = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);

  const [staff, setStaff] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { loadStaff(); }, []);

  const loadStaff = async () => {
    try {
      const response = await userService.getAllUsers();
      setStaff(response.data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load staff list.', severity: 'error' });
    }
  };

  const handleOpenAdd = () => {
    setEditingStaff(null);
    setFormData(emptyForm);
    setFormError('');
    setOpenDialog(true);
  };

  const handleOpenEdit = (row) => {
    setEditingStaff(row);
    setFormData({
      username: row.username, password: '',
      firstName: row.firstName, lastName: row.lastName,
      email: row.email || '', phoneNumber: row.phoneNumber || '',
      role: row.role,
    });
    setFormError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStaff(null);
    setFormData(emptyForm);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Manual validation so errors are always visible (MUI select can hide native tooltips)
    if (!formData.firstName.trim()) return setFormError('First Name is required.');
    if (!formData.lastName.trim())  return setFormError('Last Name is required.');
    if (!formData.username.trim())  return setFormError('Username is required.');
    if (!editingStaff && !formData.password.trim()) return setFormError('Password is required.');
    if (!formData.role)             return setFormError('Role is required.');

    setSaving(true);
    setFormError('');
    try {
      if (editingStaff) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await userService.updateUser(editingStaff.id, updateData);
        setSnackbar({ open: true, message: 'Staff updated successfully.', severity: 'success' });
      } else {
        await userService.createUser(formData);
        setSnackbar({ open: true, message: `${formData.role} account created successfully.`, severity: 'success' });
      }
      handleCloseDialog();
      loadStaff();
    } catch (err) {
      const msg = err?.response?.data?.message
        || err?.response?.data
        || 'Failed to save staff. Please check the details and try again.';
      setFormError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await userService.deleteUser(deleteConfirm.id);
      setDeleteConfirm(null);
      setSnackbar({ open: true, message: 'Staff member deleted.', severity: 'success' });
      loadStaff();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete staff member.', severity: 'error' });
    }
  };

  const columns = [
    { field: 'id',        headerName: 'ID',         width: 70 },
    { field: 'username',  headerName: 'Username',    width: 150 },
    { field: 'firstName', headerName: 'First Name',  width: 150 },
    { field: 'lastName',  headerName: 'Last Name',   width: 150 },
    { field: 'role',      headerName: 'Role',        width: 150 },
    { field: 'email',     headerName: 'Email',       width: 200 },
    {
      field: 'actions', headerName: 'Actions', width: 140, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)} title="Edit">
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => setDeleteConfirm(params.row)} title="Delete">
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const field = (key) => ({
    value: formData[key],
    onChange: (e) => { setFormData(prev => ({ ...prev, [key]: e.target.value })); setFormError(''); },
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Staff Management
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName}</Typography>
            <IconButton size="large" onClick={e => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>All Staff</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>Add Staff</Button>
          </Box>
          <DataGrid rows={staff} columns={columns} pageSize={10} autoHeight disableSelectionOnClick />
        </Paper>
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingStaff ? 'Edit Staff' : 'Add New Staff'}</DialogTitle>
        {/* noValidate disables native HTML5 validation — we handle it manually above */}
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent sx={{ pt: 2 }}>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
                {formError}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="First Name" {...field('firstName')} required />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Last Name" {...field('lastName')} required />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Username" {...field('username')} required />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={editingStaff ? 'New Password (leave blank to keep)' : 'Password'}
                  type="password"
                  {...field('password')}
                  required={!editingStaff}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth select label="Role" {...field('role')} required>
                  {ROLES.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Email" type="email" {...field('email')} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Phone Number" {...field('phoneNumber')} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving...' : editingStaff ? 'Save Changes' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Staff</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteConfirm?.firstName} {deleteConfirm?.lastName}</strong> ({deleteConfirm?.username})? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Success / Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StaffManagement;
